/**
 * Команда.
 *
 * Людей нельзя удалить — только заблокировать: удаление порвало бы
 * историю денег и ссылки в выполненных ТЗ. Приглашение выдаётся ссылкой,
 * регистрации в системе не существует.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Field, Loading, Person, toast } from '../components/ui';
import { money } from '../lib/format';
import type { Role, TeamMember } from '../lib/types';

const ROLE_NAMES: Record<Role, string> = {
  admin: 'Администратор',
  manager: 'Менеджер',
  worker: 'Съёмщик',
  editor: 'Монтажер',
};

export function TeamPage() {
  const qc = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [roles, setRoles] = useState<Role[]>(['worker']);
  /** Выданные логин и пароль — показываются один раз после создания */
  const [credentials, setCredentials] = useState<{
    displayName: string;
    username: string;
    temporaryPassword: string;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/api/users'),
  });

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ link: string }>('/api/users/invite', { displayName: name, roles }),
    onSuccess: async (res) => {
      await navigator.clipboard.writeText(res.link).catch(() => {});
      toast('Ссылка скопирована — отправь её человеку в Telegram', '⧉');
      setInviting(false);
      setName('');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  /**
   * Создание человека с логином и паролем — для тех, у кого нет Telegram
   * или он недоступен. Пароль показывается один раз, дальше он хранится
   * только в виде хеша и восстановить его нельзя, можно лишь сбросить.
   */
  const createUser = useMutation({
    mutationFn: () =>
      api.post<{ username: string; temporaryPassword: string; displayName: string }>('/api/users', {
        displayName: name,
        username: login,
        roles,
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['team'] });
      setCredentials(res);
      setInviting(false);
      setName('');
      setLogin('');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const resetPassword = useMutation({
    mutationFn: (u: TeamMember) =>
      api.post<{ temporaryPassword: string }>(`/api/users/${u.id}/reset-password`),
    onSuccess: (res, u) => {
      void qc.invalidateQueries({ queryKey: ['team'] });
      setCredentials({
        displayName: u.displayName,
        username: u.username ?? '—',
        temporaryPassword: res.temporaryPassword,
      });
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const block = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) =>
      api.post(`/api/users/${id}/${blocked ? 'unblock' : 'block'}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
      toast('Готово');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  /**
   * Правка ролей.
   *
   * Роли множественные: один человек может быть и съёмщиком, и монтажером —
   * тогда он делает видео полного цикла и получает обе суммы.
   * Сервер не даст снять роль администратора с последнего оставшегося.
   */
  const updateRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: Role[] }) =>
      api.patch(`/api/users/${id}`, { roles }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
      toast('Роли обновлены');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const toggleRole = (u: TeamMember, role: Role) => {
    const next = u.roles.includes(role)
      ? u.roles.filter((r) => r !== role)
      : [...u.roles, role];
    if (next.length === 0) return toast('Хотя бы одна роль должна остаться', '⚠');
    updateRoles.mutate({ id: u.id, roles: next });
  };

  return (
    <>
      <PageHeader
        title="Команда"
        sub={`${data?.length ?? 0} человек`}
        actions={
          <button className="btn btn-primary" onClick={() => setInviting((v) => !v)}>
            + Пригласить
          </button>
        }
      />
      <Page>
        {/* Выданные доступы. Показываются один раз — пароль восстановить нельзя */}
        {credentials && (
          <div className="card mb-4 max-w-lg border-accent/40 bg-accent/[0.06]">
            <div className="font-bold mb-1">Доступ для {credentials.displayName}</div>
            <div className="text-[11.5px] text-muted2 mb-3 leading-relaxed">
              Передай эти данные человеку. Пароль показывается один раз — дальше
              его нельзя посмотреть, только сбросить.
            </div>
            <div className="bg-bg rounded-lg px-3 py-2.5 mono text-[13.5px] space-y-1">
              <div>
                Сайт: <span className="text-accent2">{window.location.origin}</span>
              </div>
              <div>Логин: {credentials.username}</div>
              <div>Пароль: {credentials.temporaryPassword}</div>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                className="btn btn-sm flex-1"
                onClick={() => {
                  void navigator.clipboard.writeText(
                    `Сайт: ${window.location.origin}\nЛогин: ${credentials.username}\nПароль: ${credentials.temporaryPassword}`,
                  );
                  toast('Скопировано', '⧉');
                }}
              >
                Скопировать
              </button>
              <button className="btn btn-sm flex-1" onClick={() => setCredentials(null)}>
                Готово
              </button>
            </div>
            <div className="text-[11px] text-muted2 mt-2.5 leading-relaxed">
              При первом входе система попросит его придумать свой пароль —
              этот временный работать перестанет.
            </div>
          </div>
        )}

        {inviting && (
          <div className="card mb-4 max-w-lg">
            <Field label="Имя человека">
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Иванов Иван"
                autoFocus
              />
            </Field>
            <Field
              label="Логин"
              hint="Латиница и цифры, от 3 символов. Нужен для входа через сайт."
            >
              <input
                className="input mono"
                value={login}
                onChange={(e) => setLogin(e.target.value.toLowerCase())}
                placeholder="ivanov"
              />
            </Field>
            <Field label="Роли" hint="Можно выбрать несколько — например, съёмщик и монтажер">
              <div className="flex gap-2 flex-wrap">
                {(Object.keys(ROLE_NAMES) as Role[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`btn btn-sm ${roles.includes(r) ? 'btn-primary' : ''}`}
                    onClick={() =>
                      setRoles((prev) =>
                        prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r],
                      )
                    }
                  >
                    {ROLE_NAMES[r]}
                  </button>
                ))}
              </div>
            </Field>

            <button
              className="btn btn-primary w-full"
              onClick={() => createUser.mutate()}
              disabled={!name || !login || roles.length === 0 || createUser.isPending}
            >
              Создать доступ с логином и паролем
            </button>

            <div className="flex items-center gap-3 my-3">
              <div className="h-px bg-line flex-1" />
              <span className="text-[11px] text-muted2">или</span>
              <div className="h-px bg-line flex-1" />
            </div>

            <button
              className="btn w-full"
              onClick={() => invite.mutate()}
              disabled={!name || roles.length === 0 || invite.isPending}
            >
              Ссылка-приглашение в Telegram
            </button>
            <div className="text-[11.5px] text-muted2 mt-2.5 leading-relaxed">
              Первый способ надёжнее: вход через сайт работает всегда.
              Второй удобнее, но зависит от доступности Telegram.
            </div>
          </div>
        )}

        {isLoading ? (
          <Loading rows={4} />
        ) : (
          <div className="card p-0 overflow-hidden">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="text-[10.5px] tracking-[0.11em] text-muted2 font-bold uppercase">
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">Человек</th>
                  <th className="text-left px-4 pt-4 pb-2.5 border-b border-line">Роли</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line">В работе</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line">Сдано</th>
                  <th className="text-right px-4 pt-4 pb-2.5 border-b border-line">Баланс</th>
                  <th className="w-24 px-4 pt-4 pb-2.5 border-b border-line" />
                </tr>
              </thead>
              <tbody>
                {data?.map((u) => (
                  <tr key={u.id} className={u.status === 'BLOCKED' ? 'opacity-45' : ''}>
                    <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                      <Person
                        id={u.id}
                        name={u.displayName}
                        url={u.avatarUrl}
                        sub={
                          [u.username, u.tgUsername && `@${u.tgUsername}`]
                            .filter(Boolean)
                            .join(' · ') || 'доступ не настроен'
                        }
                        size={32}
                      />
                    </td>
                    <td className="px-4 py-3.5 border-b border-[#1A1A24]">
                      {/* Роли переключаются кликом. Активные подсвечены,
                          неактивные показаны бледно — сразу видно, что можно выдать */}
                      <div className="flex gap-1.5 flex-wrap">
                        {(Object.keys(ROLE_NAMES) as Role[]).map((r) => {
                          const on = u.roles.includes(r);
                          return (
                            <button
                              key={r}
                              onClick={() => toggleRole(u, r)}
                              disabled={updateRoles.isPending}
                              title={on ? 'Убрать роль' : 'Выдать роль'}
                              className={`pill transition-colors ${
                                on
                                  ? 'bg-accent/12 text-accent2 hover:bg-accent/20'
                                  : 'bg-white/[0.03] text-muted2 hover:text-muted hover:bg-white/[0.06]'
                              }`}
                            >
                              {ROLE_NAMES[r]}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 border-b border-[#1A1A24] text-right mono">
                      {u.activeVideos}
                      {u.maxActiveVideos ? <span className="text-muted2">/{u.maxActiveVideos}</span> : ''}
                    </td>
                    <td className="px-4 py-3.5 border-b border-[#1A1A24] text-right mono">
                      {u.doneVideos}
                    </td>
                    <td className="px-4 py-3.5 border-b border-[#1A1A24] text-right mono font-bold">
                      {u.balance !== undefined ? money(u.balance) : '—'}
                    </td>
                    <td className="px-4 py-3.5 border-b border-[#1A1A24] text-right">
                      <div className="flex gap-1.5 justify-end">
                        {u.username && (
                          <button
                            className="btn btn-sm"
                            title="Выдать новый временный пароль"
                            onClick={() => {
                              if (confirm(`Сбросить пароль для ${u.displayName}?`)) {
                                resetPassword.mutate(u);
                              }
                            }}
                            disabled={resetPassword.isPending}
                          >
                            Сбросить пароль
                          </button>
                        )}
                        <button
                          className="btn btn-sm"
                          onClick={() =>
                            block.mutate({ id: u.id, blocked: u.status === 'BLOCKED' })
                          }
                        >
                          {u.status === 'BLOCKED' ? 'Вернуть' : 'Заблокировать'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Page>
    </>
  );
}
